import {
  Box,
  Flex,
  Text,
  Divider,
  Button,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { DataLake, Util } from '@finley-technologies/finley-types';

import { useGetAthenaQueryResponseByRawLazyQueryGQLType } from '../requests/requests.gql.generated';
import { BindingContainer } from './BindingContainer';
import { LenderAthenaQueryPreviewDrawer } from './LenderAthenaQueryPreviewDrawer';
import { AthenaQueryStringWithSchemaSidebarContainer } from 'packages/athena/ui/AthenaQueryStringWithSchemaSidebarContainer';
import { colors } from 'packages/common-frontend/colors';
import {
  DeleteIconButton,
  CopyIconButton,
} from 'packages/common-frontend/components/IconButton';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteConfirmationModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { QueryInputTypeBadge } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantPostgresQuery/lenderView/QueryInputTypeBadge';
import { WipConfig } from 'packages/creditAgreements/ui/types';
import { AddQueryInputModal } from 'packages/settings/ui/AddQueryInputModal';

export const createAthenaQueryShell = (
  idx: number,
  name?: string,
): CreditAgreement.Config.AthenaConfig.Query => ({
  query: {
    name: name ?? `queryName${idx}`,
    queryString: 'SQL QUERY GOES HERE;',
  },
  variables: [],
  bindings: [],
  columnNames: ['placeholderColumn'],
});

type LenderAthenaQueryViewProps = {
  organizationId: string;
  athenaQuery: CreditAgreement.Config.AthenaConfig.Query;
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  duplicateParams: {
    isDuplicateQueryOpen: boolean;
    onDuplicateQueryOpen: () => void;
    onDuplicateQueryClose: () => void;
    duplicateQuery: (params: { queryKeyInput: string }) => Promise<void>;
    isDuplicateLoading: boolean;
  };
  deleteParams: {
    isDeleteConfirmationModalOpen: boolean;
    onDeleteConfirmationModalOpen: () => void;
    onDeleteConfirmationModalClose: () => void;
    deleteQuery: () => Promise<void>;
    isDeleteLoading: boolean;
  };
  changeQueryString: (params: {
    newQueryString: string;
    queryInputDateForColumnUpdate: Util.FullDateStr;
  }) => Promise<void>;
  isQueryStringSaveLoading: boolean;
  presetVariables: CreditAgreement.Config.QueryVariable[];
  validationVariables: CreditAgreement.Config.QueryVariable[];
  inputDateType: 'reportingDate' | 'triggerDate';
  selectedInputDate?: Util.FullDateStr;
};
export const LenderAthenaQueryView = ({
  organizationId,
  athenaQuery,
  wipConfig,
  fullPropertyKey,
  duplicateParams,
  deleteParams,
  changeQueryString,
  isQueryStringSaveLoading,
  presetVariables,
  validationVariables,
  inputDateType,
  selectedInputDate,
}: LenderAthenaQueryViewProps) => {
  const toast = useToast();
  const {
    isOpen: isPreviewQueryResultsDrawerOpen,
    onOpen: onPreviewQueryResultsDrawerOpen,
    onClose: onPreviewQueryResultsDrawerClose,
  } = useDisclosure();

  const [queryStringState, setQueryStringState] = useState<string>(
    athenaQuery.query.queryString,
  );
  const [isSaveQueryLoading, setIsSaveQueryLoading] = useState<boolean>(false);
  const [queryDataItems, setQueryDataItems] = useState<
    DataLake.AthenaQueryItemData[]
  >([]);

  useEffect(() => {
    setQueryStringState(athenaQuery.query.queryString);
  }, [fullPropertyKey, setQueryStringState, athenaQuery]);

  const {
    isDuplicateQueryOpen,
    onDuplicateQueryOpen,
    onDuplicateQueryClose,
    duplicateQuery,
    isDuplicateLoading,
  } = duplicateParams;
  const {
    isDeleteConfirmationModalOpen,
    onDeleteConfirmationModalOpen,
    onDeleteConfirmationModalClose,
    deleteQuery,
    isDeleteLoading,
  } = deleteParams;

  const inputDate = useMemo(
    () => selectedInputDate ?? commonDate.currentDateStr(),
    [selectedInputDate],
  );

  const [
    executeAthenaQueryByRaw,
    { loading: isQueryLoading, data: queryData, error: queryError },
  ] = useGetAthenaQueryResponseByRawLazyQueryGQLType({
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (queryData && !isQueryLoading) {
      const { Items } = queryData.athenaQueryResponse;

      setQueryDataItems(Items);
      onPreviewQueryResultsDrawerOpen();
    }
  }, [isQueryLoading, queryData, onPreviewQueryResultsDrawerOpen, toast]);

  useEffect(() => {
    if (queryError) {
      const errorDescription = commonUtils.EnvUtils.isProduction()
        ? 'Incurred server error when previewing Athena query'
        : queryError.message;
      toast({
        ...errorToastConfig,
        title: 'Athena Query Execute Error',
        description: errorDescription,
      });
    }
  }, [queryError, toast]);

  const onFullExecute = useCallback(() => {
    if (wipConfig?.isMutateAllowed(fullPropertyKey)) {
      const { variables, bindings, query } = athenaQuery;

      const supplementalVariables: CreditAgreement.Config.QueryVariable[] = [
        { name: inputDateType, value: inputDate, parseNumber: true },
      ];

      // If there's any overlap with provided "presetVariables", prioritize the
      // value that's set up with the modal through "supplementalVariables" and
      // filter out what already existed in "presetVariables"
      const updatedPresetVariables = presetVariables.filter((preset) => {
        return preset.name !== inputDateType;
      });

      executeAthenaQueryByRaw({
        variables: {
          organizationId,
          variables: [
            ...supplementalVariables,
            ...updatedPresetVariables,
            ...variables,
          ],
          bindings,
          query: query.queryString,
        },
      });
    }
  }, [
    organizationId,
    wipConfig,
    fullPropertyKey,
    athenaQuery,
    executeAthenaQueryByRaw,
    inputDateType,
    presetVariables,
    inputDate,
  ]);

  const onSave: () => Promise<void> = useCallback(async () => {
    if (!wipConfig?.isMutateAllowed(fullPropertyKey)) {
      return;
    }

    const currentFormPropsQuery = _.cloneDeep(
      getIn(wipConfig.formProps.values, fullPropertyKey),
    );
    currentFormPropsQuery.query.queryString = queryStringState;

    await wipConfig.saveAndUpdateConfigCreditAgreementWip({
      wipConfig,
      propertyKey: fullPropertyKey,
      updatedProperty: currentFormPropsQuery,
      setIsSaveLoading: setIsSaveQueryLoading,
    });
  }, [wipConfig, fullPropertyKey, queryStringState]);

  return (
    <Box>
      <Flex alignItems="center" mb="24px" justifyContent="right">
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <>
            <CopyIconButton
              ariaLabel="duplicate query"
              label="Duplicate Query"
              onClick={() => onDuplicateQueryOpen()}
            />
            <DeleteIconButton
              ariaLabel="delete query"
              label="Delete Query"
              onClick={() => onDeleteConfirmationModalOpen()}
            />
            <AddQueryInputModal
              wipConfig={wipConfig}
              isOpen={isDuplicateQueryOpen}
              onClose={onDuplicateQueryClose}
              onClickAdd={duplicateQuery}
              initialQueryType="singleQuery"
              isAddLoading={isDuplicateLoading}
              mode="duplicate"
            />
            <DeleteConfirmationModal
              isOpen={isDeleteConfirmationModalOpen}
              onClose={onDeleteConfirmationModalClose}
              onClickDelete={deleteQuery}
              isLoading={isDeleteLoading}
            />
          </>
        )}
      </Flex>
      <Divider />
      <Flex
        alignItems="center"
        justifyContent="space-between"
        mt="64px"
        mb="24px"
      >
        <Text fontWeight={600} fontSize="20px">
          Key
        </Text>
        <QueryInputTypeBadge type="singleQuery" />
      </Flex>
      <Text
        color={colors.black[70]}
        p="32px"
        borderWidth="1px"
        borderRadius="8px"
        borderColor={colors.gray[200]}
      >
        {athenaQuery.query.name}
      </Text>

      <Box mt="64px">
        {/* *** SQL Query Management *** */}
        <AthenaQueryStringWithSchemaSidebarContainer
          wipConfig={wipConfig}
          fullPropertyKey={`${fullPropertyKey}.queryString`}
          changeQueryString={changeQueryString}
          isQueryStringSaveLoading={isQueryStringSaveLoading}
          queryStringState={queryStringState}
          setQueryStringState={setQueryStringState}
          selectedInputDate={selectedInputDate}
          initialQueryString={athenaQuery.query.queryString}
        />

        {/* *** Bindings Management *** */}
        <BindingContainer
          wipConfig={wipConfig}
          bindings={athenaQuery.bindings}
          bindingsPropertyKey={`${fullPropertyKey}.bindings`}
          presetVariables={presetVariables}
          validationVariables={validationVariables}
          query={athenaQuery}
          queryType="singleQuery"
        />

        {/* *** Preview and Save Query *** */}
        {wipConfig?.isMutateAllowed(fullPropertyKey) && (
          <Flex
            alignItems="center"
            width="100%"
            justifyContent="flex-end"
            mt="64px"
          >
            <Button
              color={colors.aurora[400]}
              border="2px"
              borderRadius="8px"
              borderColor={colors.aurora[400]}
              backgroundColor="white"
              onClick={onFullExecute}
              isLoading={isQueryLoading}
              mr="24px"
            >
              Preview Query
            </Button>
            <Button
              color="white"
              backgroundColor={colors.aurora[400]}
              isLoading={isSaveQueryLoading}
              onClick={async () => {
                await onSave();
              }}
            >
              Save Query
            </Button>
            <LenderAthenaQueryPreviewDrawer
              isOpen={isPreviewQueryResultsDrawerOpen}
              onClose={() => {
                onPreviewQueryResultsDrawerClose();
                setQueryDataItems([]);
              }}
              queryDataItems={queryDataItems}
              athenaQuery={athenaQuery}
              queryType="singleQuery"
              inputDate={inputDate}
              inputDateType={inputDateType}
            />
          </Flex>
        )}
      </Box>
    </Box>
  );
};
